import React, { useState, useEffect } from 'react';
import { Phone, PhoneOff, PhoneIncoming, PhoneOutgoing, MessageSquare, Clock, Settings, Plus, Voicemail, Loader2, Search, Trash2, ExternalLink } from 'lucide-react';
import { phoneCallAPI, smsAPI, phoneProxyAPI, userAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const STATUS_BADGES = {
  incoming: { label: 'Incoming', class: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
  answered: { label: 'Answered', class: 'bg-green-500/20 text-green-500 border-green-500/30' },
  missed: { label: 'Missed', class: 'bg-red-500/20 text-red-500 border-red-500/30' },
  voicemail: { label: 'Voicemail', class: 'bg-purple-500/20 text-purple-500 border-purple-500/30' },
  outbound: { label: 'Outbound', class: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30' },
  completed: { label: 'Completed', class: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

export default function PhoneSystem() {
  const [tab, setTab] = useState('calls');
  const [calls, setCalls] = useState([]);
  const [smsMessages, setSmsMessages] = useState([]);
  const [proxies, setProxies] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Dialogs
  const [logCallOpen, setLogCallOpen] = useState(false);
  const [sendSmsOpen, setSendSmsOpen] = useState(false);
  const [addProxyOpen, setAddProxyOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Forms
  const [callForm, setCallForm] = useState({ direction: 'incoming', from_number: '', to_number: '', customer_name: '', status: 'answered', notes: '' });
  const [smsForm, setSmsForm] = useState({ from_number: '', to_number: '', body: '' });
  const [proxyForm, setProxyForm] = useState({ technician_id: '', proxy_number: '', real_number: '', purpose: 'customer_facing' });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, s, p, u] = await Promise.all([
        phoneCallAPI.list().catch(() => []),
        smsAPI.list().catch(() => []),
        phoneProxyAPI.list().catch(() => []),
        userAPI.list().catch(() => []),
      ]);
      setCalls(c); setSmsMessages(s); setProxies(p); setTechnicians(u);
    } catch (e) {
      toast.error('Failed to load phone data');
    } finally { setLoading(false); }
  };

  const handleLogCall = async (e) => {
    e.preventDefault();
    if (!callForm.from_number || !callForm.to_number) { toast.error('From & To numbers required'); return; }
    setSubmitting(true);
    try {
      await phoneCallAPI.create(callForm);
      toast.success('Call logged');
      setLogCallOpen(false);
      setCallForm({ direction: 'incoming', from_number: '', to_number: '', customer_name: '', status: 'answered', notes: '' });
      loadAll();
    } catch (e) { toast.error('Failed to log call'); }
    finally { setSubmitting(false); }
  };

  const handleSendSms = async (e) => {
    e.preventDefault();
    if (!smsForm.to_number || !smsForm.body) { toast.error('To & message required'); return; }
    setSubmitting(true);
    try {
      await smsAPI.send({ ...smsForm, direction: 'outbound' });
      toast.success('SMS logged');
      setSendSmsOpen(false);
      setSmsForm({ from_number: '', to_number: '', body: '' });
      loadAll();
    } catch (e) { toast.error('Failed to send'); }
    finally { setSubmitting(false); }
  };

  const handleAddProxy = async (e) => {
    e.preventDefault();
    if (!proxyForm.technician_id || !proxyForm.proxy_number || !proxyForm.real_number) {
      toast.error('All fields required'); return;
    }
    setSubmitting(true);
    try {
      await phoneProxyAPI.create(proxyForm);
      toast.success('Proxy added');
      setAddProxyOpen(false);
      setProxyForm({ technician_id: '', proxy_number: '', real_number: '', purpose: 'customer_facing' });
      loadAll();
    } catch (e) { toast.error('Failed to add proxy'); }
    finally { setSubmitting(false); }
  };

  const deleteProxy = async (id) => {
    if (!window.confirm('Delete this proxy?')) return;
    try {
      await phoneProxyAPI.delete(id);
      toast.success('Deleted');
      loadAll();
    } catch (e) { toast.error('Failed'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' }) : '';
  const formatDuration = (s) => {
    if (!s) return '—';
    const m = Math.floor(s / 60); const sec = s % 60;
    return `${m}m ${sec}s`;
  };

  const filteredCalls = calls.filter(c =>
    !search ||
    c.from_number?.includes(search) ||
    c.to_number?.includes(search) ||
    c.customer_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: calls.length,
    incoming: calls.filter(c => c.direction === 'incoming').length,
    missed: calls.filter(c => c.status === 'missed').length,
    sms: smsMessages.length,
    proxies: proxies.length,
  };

  return (
    <div className="space-y-6 fade-in" data-testid="phone-system-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Phone className="w-8 h-8 text-primary" />
            Phone System
          </h1>
          <p className="text-muted-foreground">Manage calls, SMS, and phone proxies</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setLogCallOpen(true)} className="btn-gradient-info" data-testid="log-call-button">
            <Phone className="w-4 h-4 mr-2" /> Log Call
          </Button>
          <Button onClick={() => setSendSmsOpen(true)} className="btn-gradient-success" data-testid="send-sms-button">
            <MessageSquare className="w-4 h-4 mr-2" /> Send SMS
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="stat-blue border"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Total Calls</p><p className="text-2xl font-bold">{stats.total}</p></CardContent></Card>
        <Card className="stat-cyan border"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Incoming</p><p className="text-2xl font-bold">{stats.incoming}</p></CardContent></Card>
        <Card className="stat-orange border"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Missed</p><p className="text-2xl font-bold text-red-500">{stats.missed}</p></CardContent></Card>
        <Card className="stat-green border"><CardContent className="p-3"><p className="text-xs text-muted-foreground">SMS Messages</p><p className="text-2xl font-bold">{stats.sms}</p></CardContent></Card>
        <Card className="stat-purple border"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Phone Proxies</p><p className="text-2xl font-bold">{stats.proxies}</p></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full justify-start">
          <TabsTrigger value="calls"><Phone className="w-4 h-4 mr-2" />Call History</TabsTrigger>
          <TabsTrigger value="sms"><MessageSquare className="w-4 h-4 mr-2" />SMS</TabsTrigger>
          <TabsTrigger value="proxies"><Settings className="w-4 h-4 mr-2" />Phone Proxies</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input placeholder="Search by phone or name..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
      </div>

      {loading ? (
        <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></CardContent></Card>
      ) : (
        <>
          {/* CALLS */}
          {tab === 'calls' && (
            filteredCalls.length === 0 ? (
              <Card><CardContent className="p-12 text-center"><Phone className="w-16 h-16 mx-auto text-muted-foreground mb-3" /><p className="text-muted-foreground">No calls logged yet</p></CardContent></Card>
            ) : (
              <div className="space-y-2">
                {filteredCalls.map(c => {
                  const sc = STATUS_BADGES[c.status] || { label: c.status, class: '' };
                  const Icon = c.direction === 'incoming' ? PhoneIncoming : PhoneOutgoing;
                  return (
                    <Card key={c.id} className="card-hover" data-testid={`call-${c.id}`}>
                      <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${c.direction === 'incoming' ? 'bg-blue-500/10' : 'bg-cyan-500/10'}`}>
                            <Icon className={`w-5 h-5 ${c.direction === 'incoming' ? 'text-blue-500' : 'text-cyan-500'}`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold">{c.customer_name || c.from_number}</p>
                              <Badge variant="outline" className={`${sc.class} text-xs border`}>{sc.label}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {c.direction === 'incoming' ? `From ${c.from_number}` : `To ${c.to_number}`} • {formatDate(c.started_at)} • {formatDuration(c.duration_seconds)}
                            </p>
                            {c.notes && <p className="text-xs mt-1 text-muted-foreground">📝 {c.notes}</p>}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <a href={`tel:${c.from_number}`} className="text-primary hover:underline text-sm flex items-center gap-1"><Phone className="w-3 h-3" />Call</a>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )
          )}

          {/* SMS */}
          {tab === 'sms' && (
            smsMessages.length === 0 ? (
              <Card><CardContent className="p-12 text-center"><MessageSquare className="w-16 h-16 mx-auto text-muted-foreground mb-3" /><p className="text-muted-foreground">No SMS messages yet</p></CardContent></Card>
            ) : (
              <div className="space-y-2">
                {smsMessages.map(m => (
                  <Card key={m.id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className={m.direction === 'incoming' ? 'bg-blue-500/10 text-blue-500' : 'bg-green-500/10 text-green-500'}>
                            {m.direction === 'incoming' ? '← Received' : '→ Sent'}
                          </Badge>
                          <span className="text-sm font-semibold">{m.customer_name || (m.direction === 'incoming' ? m.from_number : m.to_number)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{formatDate(m.created_at)}</span>
                      </div>
                      <p className="text-sm bg-muted/30 p-3 rounded">{m.body}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          )}

          {/* PROXIES */}
          {tab === 'proxies' && (
            <>
              <Button onClick={() => setAddProxyOpen(true)} className="btn-gradient-primary" data-testid="add-proxy-button">
                <Plus className="w-4 h-4 mr-2" /> Add Phone Proxy
              </Button>
              {proxies.length === 0 ? (
                <Card><CardContent className="p-12 text-center"><Settings className="w-16 h-16 mx-auto text-muted-foreground mb-3" /><p className="text-muted-foreground">No phone proxies configured</p><p className="text-xs text-muted-foreground mt-1">Proxies let customers call a single number that forwards to the right technician</p></CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {proxies.map(p => {
                    const tech = technicians.find(t => t.id === p.technician_id);
                    return (
                      <Card key={p.id} className="card-hover">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold">{tech ? `${tech.first_name} ${tech.last_name}` : 'Unknown Tech'}</p>
                              <p className="text-sm text-muted-foreground mt-1">📞 Public: <span className="font-mono">{p.proxy_number}</span></p>
                              <p className="text-sm text-muted-foreground">🔒 Real: <span className="font-mono">{p.real_number}</span></p>
                              {p.purpose && <Badge variant="secondary" className="mt-2 text-xs">{p.purpose}</Badge>}
                            </div>
                            <Button size="sm" variant="outline" onClick={() => deleteProxy(p.id)} className="text-destructive">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Log Call Dialog */}
      <Dialog open={logCallOpen} onOpenChange={setLogCallOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Phone Call</DialogTitle><DialogDescription>Record a call for tracking</DialogDescription></DialogHeader>
          <form onSubmit={handleLogCall} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Direction</Label>
                <Select value={callForm.direction} onValueChange={(v) => setCallForm({ ...callForm, direction: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="incoming">Incoming</SelectItem><SelectItem value="outbound">Outbound</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={callForm.status} onValueChange={(v) => setCallForm({ ...callForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="answered">Answered</SelectItem>
                    <SelectItem value="missed">Missed</SelectItem>
                    <SelectItem value="voicemail">Voicemail</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>From Number *</Label><Input value={callForm.from_number} onChange={(e) => setCallForm({ ...callForm, from_number: e.target.value })} placeholder="555-1234" required /></div>
            <div className="space-y-2"><Label>To Number *</Label><Input value={callForm.to_number} onChange={(e) => setCallForm({ ...callForm, to_number: e.target.value })} placeholder="555-5678" required /></div>
            <div className="space-y-2"><Label>Customer Name</Label><Input value={callForm.customer_name} onChange={(e) => setCallForm({ ...callForm, customer_name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={callForm.notes} onChange={(e) => setCallForm({ ...callForm, notes: e.target.value })} rows={2} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setLogCallOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log Call'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Send SMS Dialog */}
      <Dialog open={sendSmsOpen} onOpenChange={setSendSmsOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send SMS</DialogTitle><DialogDescription>Log an outbound text (requires Twilio integration to actually send)</DialogDescription></DialogHeader>
          <form onSubmit={handleSendSms} className="space-y-3">
            <div className="space-y-2"><Label>From Number</Label><Input value={smsForm.from_number} onChange={(e) => setSmsForm({ ...smsForm, from_number: e.target.value })} placeholder="Your business number" /></div>
            <div className="space-y-2"><Label>To Number *</Label><Input value={smsForm.to_number} onChange={(e) => setSmsForm({ ...smsForm, to_number: e.target.value })} placeholder="Customer phone" required /></div>
            <div className="space-y-2"><Label>Message *</Label><Textarea value={smsForm.body} onChange={(e) => setSmsForm({ ...smsForm, body: e.target.value })} rows={4} required /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSendSmsOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-success" disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Proxy Dialog */}
      <Dialog open={addProxyOpen} onOpenChange={setAddProxyOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Phone Proxy</DialogTitle><DialogDescription>Map a public-facing number to a technician's real number</DialogDescription></DialogHeader>
          <form onSubmit={handleAddProxy} className="space-y-3">
            <div className="space-y-2"><Label>Technician *</Label>
              <Select value={proxyForm.technician_id} onValueChange={(v) => setProxyForm({ ...proxyForm, technician_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{technicians.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Public/Proxy Number *</Label><Input value={proxyForm.proxy_number} onChange={(e) => setProxyForm({ ...proxyForm, proxy_number: e.target.value })} placeholder="555-PROXY" required /></div>
            <div className="space-y-2"><Label>Real Number *</Label><Input value={proxyForm.real_number} onChange={(e) => setProxyForm({ ...proxyForm, real_number: e.target.value })} placeholder="555-REAL" required /></div>
            <div className="space-y-2"><Label>Purpose</Label>
              <Select value={proxyForm.purpose} onValueChange={(v) => setProxyForm({ ...proxyForm, purpose: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer_facing">Customer Facing</SelectItem>
                  <SelectItem value="field_calls">Field Calls</SelectItem>
                  <SelectItem value="emergency">Emergency Line</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddProxyOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Proxy'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
